import React, { useMemo, useState } from 'react';
import Modal from '~ComponentsImproved/Modal';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { BlueButton, Box, RedButton } from '~Styles';
import ErrorRow from '../ErrorRow';
import { ChangePrinterWrapper, ChangePrinterRow } from '../styles';
import { DateTime } from 'luxon';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useDispatch, useI18n, usePrinterApi, useSelector } from '~Hooks';
import * as actions from '~actions';
import Heading from '~ComponentsImproved/Typography/Heading';

const FailedPrints = () => {
  const [printerIdToChangeTo, setPrinterIdToChangeTo] = useState<string | null>(null);
  const printerApi = usePrinterApi();
  const changePrinterModal = useModal();
  const dispatch = useDispatch();
  const { i18n } = useI18n();

  const printers = useSelector((state) => state.printers);
  const printQueue = useSelector((state) => state.POSPrintQueue);
  const printErrors = useMemo(() => printQueue.filter((print) => Boolean(print.error)), [
    printQueue,
  ]);

  const sortedPrintErrors = useMemo(() => {
    return [...printErrors].sort((a, b) => {
      const timeA = a.lastPrinted ? DateTime.fromJSDate(a.lastPrinted).toMillis() : -Infinity;
      const timeB = b.lastPrinted ? DateTime.fromJSDate(b.lastPrinted).toMillis() : -Infinity;

      return timeB - timeA;
    });
  }, [printErrors]);

  const dismissAllButton = useButton({
    onClick: () => {
      printErrors.forEach((print) => {
        dispatch(actions.removePrintFromQueue(print.id));
      });
    },
  });

  const tryAllAgain = useButton({
    onClick: () => {
      printErrors.forEach((print) => {
        dispatch(actions.printQueue(print.id));
      });
    },
  });

  return (
    <div>
      <Box fluid display="flex" flexDirection="column" textAlign="left" gap="xs">
        <Box marginTop="xs" marginBottom="xs" display="flex" gap="xs">
          <RedButton {...dismissAllButton}>{i18n('Printer.Status.DismissAll')}</RedButton>
          <BlueButton {...tryAllAgain}>{i18n('Printer.Status.TryAllAgain')}</BlueButton>
        </Box>

        {sortedPrintErrors
          .filter((p) => !p.reprintOfPrintId)
          .map((print) => (
            <ErrorRow
              key={print.id}
              print={print}
              onPrinterChange={(id) => {
                setPrinterIdToChangeTo(id);
                changePrinterModal.show();
              }}
            />
          ))}
      </Box>

      {/* Showed if the retry-with button is clicked in ErrorRow */}
      <Modal isShowing={changePrinterModal.isShowing} hide={changePrinterModal.hide}>
        <ChangePrinterWrapper>
          {printers.map((printer) => (
            <ChangePrinterRow
              onTouchStart={() => {
                dispatch(actions.changePrintQueuePrinter(printerIdToChangeTo!, printer.id));
                dispatch(actions.printQueue(printerIdToChangeTo!));
                changePrinterModal.hide();
              }}>
              <Heading level={3} bottom={4}>
                {printerApi.getNameFromId(printer.id) || printer.id}
              </Heading>
              <Paragraph level={4}>{printer.model}</Paragraph>
            </ChangePrinterRow>
          ))}
        </ChangePrinterWrapper>
      </Modal>
    </div>
  );
};

export default FailedPrints;
